import { getCurrentUser, readAuthSafe, signOut } from "./auth";

export const EDI_API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";

const ERROR_CODE_MESSAGES = {
  PO_DETAILS_CONFIRMATION_REQUIRED: "Current PO Details status requires explicit confirmation.",
  AUTHENTICATION_REQUIRED: "Please sign in to access EDI resources.",
  AUTHENTICATION_UNAVAILABLE: "Authentication service is unavailable.",
  FORBIDDEN: "You do not have Full permission for this 997 workflow action.",
  PERMISSION_UNAVAILABLE: "Unable to verify EDI permission.",
  EDI_DRAFT_NOT_FOUND: "The 997 draft was not found.",
  EDI_ARTIFACT_NOT_FOUND: "The complete 997 artifact was not found.",
  EDI_STALE_ARTIFACT: "The 997 artifact changed. Refresh the draft and try again.",
  EDI_WORKFLOW_CONFLICT: "The 997 workflow status changed. Refresh the draft and try again.",
  EDI_WORKFLOW_VALIDATION_FAILED: "The 997 could not be processed because its configuration or source data is invalid.",
  EDI_WORKFLOW_UNAVAILABLE: "The 997 workflow service is temporarily unavailable.",
  EDI_SCHEMA_NOT_READY: "The EDI acknowledgement schema is not ready.",
  INVALID_REQUEST: "The 997 workflow request was invalid.",
  LOAD_PO_NOT_FOUND: "Shipment/PO was not found in the active customer scope.",
  "856_WORKFLOW_SCOPE_AMBIGUOUS": "The shipment could not be resolved safely.",
  "856_PREVIEW_NOT_READY": "The 856 is not ready to generate.",
  "856_ARTIFACT_INTEGRITY_FAILED": "The generated 856 failed its integrity check. Contact IT.",
  "856_SOURCE_CHANGED_AFTER_GENERATION": "Shipment data changed after the 856 was generated. Contact IT.",
  "856_CONFIRMATION_TOKEN_INVALID": "The review is stale. Review the 856 again before sending.",
  LOAD_NOT_FOUND: "The Walmart load was not found in the active customer scope.",
  "810_GENERATION_NOT_FOUND": "The generated Walmart 810 was not found.",
  "810_ARTIFACT_NOT_FOUND": "The generated Walmart 810 artifact was not found.",
  "810_ARTIFACT_INTEGRITY_FAILED": "The generated Walmart 810 failed its integrity check. Contact IT.",
  "810_CONFIRMATION_REQUIRED": "Explicit confirmation is required before sending the Walmart 810.",
  "810_CONFIRMATION_TOKEN_INVALID": "The review is stale. Prepare the Walmart 810 again before sending.",
  "810_TRANSMISSION_CLAIM_UNAVAILABLE": "The Walmart 810 transmission could not be claimed safely. Do not retry until IT reviews it.",
  TRANSPORT_OUTCOME_UNKNOWN: "The transmission outcome is unknown. Do not resend. Contact IT.",
  POST_SUBMISSION_PERSISTENCE_FAILED: "The 856 was submitted but PortPilot could not finish local persistence. Do not resend. Contact IT.",
};

export class EdiApiError extends Error {
  constructor(message, { status = 0, errorCode = null, details = null } = {}) {
    super(message);
    this.name = "EdiApiError";
    this.status = status;
    this.errorCode = errorCode;
    this.details = details;
  }
}

function authHeaders(download = false) {
  const auth = readAuthSafe();
  if (!auth?.token) throw new EdiApiError(download ? "Please sign in to download raw X12." : "Please sign in to access EDI resources.", { status: 401, errorCode: "AUTHENTICATION_REQUIRED" });
  const user = getCurrentUser();
  return { Authorization: `Bearer ${auth.token}`, "X-User-Email": user?.email || "", "X-User-Role": user?.role || "" };
}

function statusError(response, download = false, serverError = "", errorCode = null) {
  if (response.status === 401) { signOut({ revokeRemote: false }); return download ? "Please sign in to download raw X12." : "Please sign in to access EDI resources."; }
  if (errorCode && ERROR_CODE_MESSAGES[errorCode]) return ERROR_CODE_MESSAGES[errorCode];
  if (response.status === 403) return download ? "Raw X12 download requires Full permission." : "You do not have permission to view this EDI resource.";
  if (response.status === 404) return download ? "The requested raw X12 was not found." : "The requested EDI resource was not found.";
  if (response.status === 409) return "The EDI workflow state changed. Please refresh and try again.";
  if (response.status === 422) return "The EDI request could not be validated.";
  if (response.status === 503) {
    if (serverError === "Authentication service is unavailable") return "Authentication service is unavailable.";
    if (serverError === "Unable to verify permission") return "Unable to verify EDI permission.";
    if (serverError === "EDI acknowledgement schema is not ready") return "EDI acknowledgement schema is not ready.";
    return "The EDI service is temporarily unavailable.";
  }
  return null;
}

export async function ediRequest(path, { method = "GET", body, responseType = "json", headers: extraHeaders = {} } = {}) {
  let response;
  const headers = { ...authHeaders(responseType === "blob"), ...extraHeaders };
  const options = { method, headers };
  if (body !== undefined) { headers["Content-Type"] = "application/json"; options.body = JSON.stringify(body); }
  try { response = await fetch(`${EDI_API_BASE}${path}`, options); }
  catch { throw new EdiApiError("Unable to reach the EDI service."); }
  if (responseType === "blob") return response;
  let value;
  try { value = await response.json(); } catch { throw new EdiApiError("The EDI service returned an invalid response.", { status: response.status }); }
  if (!response.ok || value?.ok !== true) {
    const errorCode = typeof value?.error_code === "string" ? value.error_code : null;
    const mapped = statusError(response, false, value?.error || "", errorCode);
    const details = errorCode === "PO_DETAILS_CONFIRMATION_REQUIRED"
      ? { po_details_check: value?.po_details_check || null }
      : null;
    throw new EdiApiError(mapped || value?.error || "Unable to load EDI data.", { status: response.status, errorCode, details });
  }
  return value;
}

export async function ediJson(path, options) { return ediRequest(path, options); }

export function get856Preview(loadId, { headers = {} } = {}) {
  const query = new URLSearchParams({ load_id: String(loadId) });
  return ediRequest(`/api/edi/856-preview?${query.toString()}`, { headers });
}

export function get856Reconciliation(shippedDate, { headers = {} } = {}) {
  const query = new URLSearchParams({ shipped_date: String(shippedDate) });
  return ediRequest(`/api/edi/856-reconciliation?${query.toString()}`, { headers });
}

export function refresh856Reconciliation(shippedDate, { headers = {} } = {}) {
  return ediRequest("/api/edi/856-reconciliation/refresh", {
    method: "POST", body: { shipped_date: String(shippedDate) }, headers,
  });
}

export function get856Workflow(loadId, poNumber, { headers = {} } = {}) {
  const query = new URLSearchParams({ load_id: String(loadId), po_number: String(poNumber) });
  return ediRequest(`/api/edi/856-workflow?${query.toString()}`, { headers });
}

export function generate856(loadId, poNumber, { headers = {} } = {}) {
  return ediRequest("/api/edi/856-generations", { method: "POST", body: { load_id: String(loadId), po_number: String(poNumber) }, headers });
}

export function prepare856Submission(generationId, { headers = {} } = {}) {
  return ediRequest(`/api/edi/856-generations/${generationId}/submission/prepare`, { method: "POST", body: {}, headers });
}

export function send856Submission(generationId, confirmationToken, { headers = {}, differencesConfirmed } = {}) {
  return ediRequest(`/api/edi/856-generations/${generationId}/submission/send`, { method: "POST", body: { confirmed: true, confirmation_token: confirmationToken, ...(differencesConfirmed === undefined ? {} : { differences_confirmed: differencesConfirmed }) }, headers });
}

export async function get810Preview(loadId, poNumber, { headers = {} } = {}) {
  const query = new URLSearchParams({ load_id: String(loadId), po_number: String(poNumber) });
  const value = await ediRequest(`/api/edi/810-preview?${query.toString()}`, { headers });
  return value.preview;
}

export function get810Reconciliation(shippedDate, { headers = {} } = {}) {
  const query = new URLSearchParams({ shipped_date: String(shippedDate) });
  return ediRequest(`/api/edi/810-reconciliation?${query.toString()}`, { headers });
}

export function get810Batch(loadId, { headers = {} } = {}) {
  const query = new URLSearchParams({ load_id: String(loadId) });
  return ediRequest(`/api/edi/810-batch?${query.toString()}`, { headers });
}

export async function get810Workflow(loadId, poNumber, { headers = {} } = {}) {
  const query = new URLSearchParams({ load_id: String(loadId), po_number: String(poNumber) });
  const value = await ediRequest(`/api/edi/810-workflow?${query.toString()}`, { headers });
  return value.workflow;
}

export async function generate810(loadId, poNumber, invoiceDate, { headers = {} } = {}) {
  const value = await ediRequest("/api/edi/810-generate", {
    method: "POST",
    body: { load_id: String(loadId), po_number: String(poNumber), invoice_date: String(invoiceDate) },
    headers,
  });
  return value.generation;
}

export async function prepare810Submission(generationId, { headers = {} } = {}) {
  const value = await ediRequest(`/api/edi/810-generations/${generationId}/prepare`, { method: "POST", body: {}, headers });
  return value.submission;
}

export async function send810Submission(generationId, confirmationToken, { headers = {} } = {}) {
  const value = await ediRequest(`/api/edi/810-generations/${generationId}/send`, {
    method: "POST",
    body: { confirmed: true, confirmation_token: confirmationToken },
    headers,
  });
  return value.submission;
}

export async function downloadEdi(path, filename) {
  let response;
  response = await ediRequest(path, { responseType: "blob" });
  if (!response.ok) {
    let serverError = ""; let errorCode = null;
    try { const value = await response.json(); serverError = value?.error || ""; errorCode = value?.error_code || null; } catch { /* malformed errors use the safe status message */ }
    const mapped = statusError(response, true, serverError, errorCode);
    if (mapped) throw new EdiApiError(mapped, { status: response.status, errorCode });
    throw new EdiApiError(serverError || "Unable to download raw X12.", { status: response.status, errorCode });
  }
  const blob = await response.blob();
  let url;
  try {
    url = URL.createObjectURL(blob);
    const disposition = response.headers.get("Content-Disposition") || "";
    const match = disposition.match(/filename="?([^";\\/]+)"?/i);
    const safeName = match?.[1]?.replace(/[^a-zA-Z0-9._-]/g, "_") || filename;
    const link = document.createElement("a"); link.href = url; link.download = safeName; link.click();
  } finally {
    if (url) URL.revokeObjectURL(url);
  }
}

export function generate997Artifact(draftId) {
  return ediRequest(`/api/edi/acknowledgement-drafts/${draftId}/generate`, { method: "POST", body: {} });
}

function transitionBody(expectedRawSha256, note) {
  const body = { expected_raw_sha256: expectedRawSha256 };
  if (note !== undefined && note !== null && note !== "") body.note = note;
  return body;
}

export function review997Artifact(draftId, { expectedRawSha256, note } = {}) {
  return ediRequest(`/api/edi/acknowledgement-drafts/${draftId}/review`, {
    method: "POST",
    body: transitionBody(expectedRawSha256, note),
  });
}

export function approve997Artifact(draftId, { expectedRawSha256, note } = {}) {
  return ediRequest(`/api/edi/acknowledgement-drafts/${draftId}/approve`, {
    method: "POST",
    body: transitionBody(expectedRawSha256, note),
  });
}

export function send997Artifact(draftId) {
  return ediRequest(`/api/edi/acknowledgement-drafts/${draftId}/send`, { method: "POST", body: {} });
}

export function prepare997Submission(draftId) {
  return ediRequest(`/api/edi/acknowledgement-drafts/${draftId}/submission/prepare`, { method: "POST", body: {} });
}

export function confirm997Submission(draftId) {
  return ediRequest(`/api/edi/acknowledgement-drafts/${draftId}/submission/confirm`, { method: "POST", body: { confirmed: true } });
}

export function send997Submission(draftId, { confirmNonMatchedPoDetails = false, poDetailsConfirmationToken = null } = {}) {
  const body = { confirmed: true };
  if (confirmNonMatchedPoDetails) {
    body.confirm_non_matched_po_details = true;
    body.po_details_confirmation_token = poDetailsConfirmationToken;
  }
  return ediRequest(`/api/edi/acknowledgement-drafts/${draftId}/submission/send`, { method: "POST", body });
}

export function download997Artifact(draftId, fallbackFilename) {
  return downloadEdi(`/api/edi/acknowledgement-drafts/${draftId}/artifact/raw`, fallbackFilename);
}
